import { fmt, t as catalogue } from './i18n';
import type {
  ClientMessage,
  CreateRoomInput,
  ErrorCode,
  GameAction,
  GameEvent,
  RoomRules,
  RoomSummary,
  RoomView,
  ServerMessage,
} from '@uno/shared';

export type ConnectionStatus = 'connecting' | 'online' | 'offline';

export interface Toast {
  id: number;
  kind: 'info' | 'error' | 'penalty' | 'success';
  text: string;
}

export interface FeedEvent {
  id: number;
  event: GameEvent;
  at: number;
}

export interface AppState {
  status: ConnectionStatus;
  sessionId: string | null;
  nickname: string;
  /** Chosen avatar emoji; '' means fall back to the nickname's initial. */
  emoji: string;
  /**
   * Whether this visitor has picked a name themselves. The server hands out a
   * placeholder so a session always has one, which is exactly why we track the
   * deliberate choice separately — otherwise first-timers never get prompted.
   */
  hasChosenName: boolean;
  rooms: RoomSummary[];
  /** What we render. May briefly hold an optimistic overlay. */
  room: RoomView | null;
  /** Last snapshot the server confirmed — the rollback target. */
  authoritative: RoomView | null;
  /** An action is in flight; inputs are locked until the server answers. */
  pending: boolean;
  feed: FeedEvent[];
  toasts: Toast[];
  joinError: string | null;
  /** Set when a join bounced for want of a password — the UI opens the prompt. */
  passwordPromptFor: string | null;
}

const LS_SESSION = 'uno:sessionId';
const LS_NICKNAME = 'uno:nickname';
const LS_NAMED = 'uno:named';
const LS_EMOJI = 'uno:emoji';
const FEED_MAX = 40;
/** If the server never answers, unlock the UI rather than soft-locking the player. */
const ACTION_TIMEOUT_MS = 6000;

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

function socketUrl(): string {
  const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${location.host}/ws`;
}

/**
 * Predicts the result of our own action so the UI reacts instantly.
 *
 * This is presentation only — the server is the sole authority. Whatever it
 * sends back (a fresh snapshot, or `action_rejected` carrying the true state)
 * overwrites this wholesale, which is the rollback.
 */
function applyOptimistic(room: RoomView, action: GameAction): RoomView {
  if (action.type !== 'play' || !room.self) return room;
  const card = room.self.hand.find((c) => c.id === action.cardId);
  if (!card) return room;

  const shown = action.chosenColor ? { ...card, color: action.chosenColor } : card;
  return {
    ...room,
    game: {
      ...room.game,
      topCard: shown,
      activeColor: action.chosenColor ?? (card.color === 'wild' ? room.game.activeColor : card.color),
      turnPlayerId: null,
    },
    players: room.players.map((p) =>
      p.id === room.self!.id ? { ...p, handCount: Math.max(0, p.handCount - 1) } : p,
    ),
    self: {
      ...room.self,
      hand: room.self.hand.filter((c) => c.id !== action.cardId),
      playableCardIds: [],
      drawnCardId: null,
      isMyTurn: false,
      canCallUno: false,
    },
  };
}

export class Net {
  private ws: WebSocket | null = null;
  private listeners = new Set<() => void>();
  private reconnectAttempts = 0;
  private reconnectTimer: number | null = null;
  private actionTimer: number | null = null;
  private nextId = 1;
  private closed = false;
  /** Messages sent before the session handshake completed, replayed after it. */
  private queue: ClientMessage[] = [];
  private ready = false;
  /** A room code from a `/r/CODE` deep link, joined once the session is up. */
  private pendingJoin: string | null = null;

  state: AppState = {
    status: 'connecting',
    sessionId: read(LS_SESSION),
    nickname: read(LS_NICKNAME) ?? '',
    emoji: read(LS_EMOJI) ?? '',
    hasChosenName: read(LS_NAMED) === '1',
    rooms: [],
    room: null,
    authoritative: null,
    pending: false,
    feed: [],
    toasts: [],
    joinError: null,
    passwordPromptFor: null,
  };

  /** The room id of the most recent join attempt, for the password retry. */
  private lastJoinRoomId: string | null = null;

  /* --- store plumbing --------------------------------------------- */

  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  getSnapshot = (): AppState => this.state;

  private patch(next: Partial<AppState>): void {
    this.state = { ...this.state, ...next };
    for (const fn of this.listeners) fn();
  }

  /* --- connection -------------------------------------------------- */

  connect(): void {
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) return;
    this.closed = false;
    this.patch({ status: this.state.authoritative ? 'connecting' : 'connecting' });

    const ws = new WebSocket(socketUrl());
    this.ws = ws;

    ws.onopen = () => {
      this.reconnectAttempts = 0;
      this.patch({ status: 'online' });
      // `hello` re-attaches to the existing session, restoring an in-flight game.
      this.send({
        t: 'hello',
        ...(this.state.sessionId ? { sessionId: this.state.sessionId } : {}),
        ...(this.state.nickname ? { nickname: this.state.nickname } : {}),
      });
    };

    ws.onmessage = (ev) => {
      let msg: ServerMessage;
      try {
        msg = JSON.parse(ev.data as string) as ServerMessage;
      } catch {
        return;
      }
      this.handle(msg);
    };

    ws.onclose = () => {
      this.ws = null;
      this.ready = false;
      // Anything still queued was built against state we no longer trust.
      this.queue = [];
      this.clearActionTimer();
      this.patch({ status: 'offline', pending: false });
      if (!this.closed) this.scheduleReconnect();
    };

    ws.onerror = () => {
      /* onclose handles it */
    };
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer !== null) return;
    const delay = Math.min(8000, 400 * 2 ** this.reconnectAttempts++);
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  disconnect(): void {
    this.closed = true;
    this.ws?.close();
  }

  /**
   * `hello` goes out the moment the socket opens; everything else waits for the
   * server to answer it. Without this, anything sent during mount (the lobby's
   * room list, a deep-link join) would be dropped on the floor.
   */
  private send(msg: ClientMessage): void {
    const open = this.ws?.readyState === WebSocket.OPEN;
    if (msg.t === 'hello') {
      if (open) this.ws!.send(JSON.stringify(msg));
      return;
    }
    if (!open || !this.ready) {
      if (this.queue.length < 16) this.queue.push(msg);
      return;
    }
    this.ws!.send(JSON.stringify(msg));
  }

  private flushQueue(): void {
    const queued = this.queue;
    this.queue = [];
    for (const msg of queued) this.send(msg);
  }

  /**
   * Records a room code from the URL. It is joined once the session handshake
   * lands — and skipped if the server puts us back in a room on its own, which
   * is what happens when you refresh mid-game.
   */
  setPendingJoin(roomId: string | null): void {
    this.pendingJoin = roomId;
  }

  /* --- inbound ----------------------------------------------------- */

  private handle(msg: ServerMessage): void {
    switch (msg.t) {
      case 'session': {
        write(LS_SESSION, msg.sessionId);
        write(LS_NICKNAME, msg.nickname);
        write(LS_EMOJI, msg.emoji);
        this.patch({ sessionId: msg.sessionId, nickname: msg.nickname, emoji: msg.emoji });
        this.ready = true;
        this.flushQueue();

        const deepLink = this.pendingJoin;
        this.pendingJoin = null;
        // The server already restored a room (mid-game refresh) — leave it be.
        if (deepLink && !msg.roomId) this.joinRoom(deepLink);
        return;
      }
      case 'rooms':
        this.patch({ rooms: msg.rooms });
        return;

      case 'room': {
        this.clearActionTimer();
        this.patch({
          room: msg.room,
          authoritative: msg.room,
          pending: false,
          joinError: null,
          feed: this.appendFeed(msg.events),
        });
        this.narrate(msg.room, msg.events);
        return;
      }

      case 'action_rejected': {
        this.clearActionTimer();
        // Discard the optimistic prediction and adopt the server's truth.
        this.patch({ room: msg.room, authoritative: msg.room, pending: false });
        this.toast(
          msg.code === 'stale_action' ? 'info' : 'error',
          this.localizeError(msg.code, msg.message),
        );
        return;
      }

      case 'left_room':
        this.patch({ room: null, authoritative: null, pending: false, feed: [] });
        return;

      case 'error': {
        this.clearActionTimer();
        this.patch({ pending: false });
        if (msg.code === 'not_in_room') {
          // The room went away underneath us (reaped, or we were removed).
          this.patch({ room: null, authoritative: null, feed: [] });
          this.listRooms();
        }
        const text = this.localizeError(msg.code, msg.message);
        if (msg.code === 'wrong_password' || msg.code === 'room_not_found' || msg.code === 'room_full' || msg.code === 'game_in_progress') {
          this.patch({ joinError: text });
        }
        // A locked room should ask for the password, not just complain.
        if (msg.code === 'wrong_password' && this.lastJoinRoomId) {
          this.patch({ passwordPromptFor: this.lastJoinRoomId });
        }
        this.toast('error', text);
        return;
      }

      case 'pong':
        return;
    }
  }

  private appendFeed(events: GameEvent[]): FeedEvent[] {
    if (!events.length) return this.state.feed;
    const at = Date.now();
    const added = events.map((event) => ({ id: this.nextId++, event, at }));
    return [...this.state.feed, ...added].slice(-FEED_MAX);
  }

  /**
   * Turns notable events into toasts.
   *
   * Reads the catalogue at call time rather than capturing it, so a language
   * change takes effect on the very next event without a reload.
   */
  private narrate(room: RoomView, events: GameEvent[]): void {
    const T = catalogue();
    const nameOf = (id: string) => room.players.find((p) => p.id === id)?.nickname ?? T.events.someone;
    const me = room.self?.id;

    for (const e of events) {
      switch (e.type) {
        case 'judged':
          this.toast(
            e.targetId === me ? 'penalty' : 'info',
            e.targetId === me
              ? fmt(T.events.judgedYou, { accuser: nameOf(e.accuserId), count: e.penalty })
              : fmt(T.events.judged, {
                  accuser: nameOf(e.accuserId),
                  target: nameOf(e.targetId),
                  count: e.penalty,
                }),
          );
          break;
        case 'uno_called':
          this.toast(
            'success',
            e.playerId === me ? T.events.unoCalledYou : fmt(T.events.unoCalledOther, { name: nameOf(e.playerId) }),
          );
          break;
        case 'challenge_resolved':
          this.toast(
            'info',
            e.bluffed
              ? fmt(T.events.challengeBluff, { name: nameOf(e.targetId), count: e.penalty })
              : fmt(T.events.challengeFailed, { name: nameOf(e.accuserId), count: e.penalty }),
          );
          break;
        case 'player_forfeited':
          this.toast('info', fmt(T.events.forfeited, { name: nameOf(e.playerId) }));
          break;
        case 'afk_autoplay':
          this.toast(
            'info',
            e.playerId === me ? T.events.afkYou : fmt(T.events.afkOther, { name: nameOf(e.playerId) }),
          );
          break;
        case 'host_changed':
          this.toast('info', fmt(T.events.hostChanged, { name: nameOf(e.playerId) }));
          break;
        case 'deck_reshuffled':
          this.toast('info', T.events.reshuffled);
          break;
        case 'game_over': {
          if (!e.winnerId) {
            this.toast('success', T.events.gameOver);
          } else {
            this.toast(
              'success',
              e.winnerId === me ? T.events.youWin : fmt(T.events.winner, { name: nameOf(e.winnerId) }),
            );
          }
          break;
        }
      }
    }
  }

  /**
   * The server's message text is English and developer-facing. The `code` is
   * the contract, so the UI renders from that and keeps the text as a fallback
   * for any code a future server adds before this client knows about it.
   */
  private localizeError(code: ErrorCode, fallback: string): string {
    const messages = catalogue().errors as Record<string, string | undefined>;
    return messages[code] ?? fallback;
  }

  /* --- toasts ------------------------------------------------------ */

  toast(kind: Toast['kind'], text: string): void {
    const toast: Toast = { id: this.nextId++, kind, text };
    this.patch({ toasts: [...this.state.toasts, toast].slice(-4) });
    window.setTimeout(() => {
      this.patch({ toasts: this.state.toasts.filter((t) => t.id !== toast.id) });
    }, 4000);
  }

  dismissToast(id: number): void {
    this.patch({ toasts: this.state.toasts.filter((t) => t.id !== id) });
  }

  /* --- outbound API ------------------------------------------------ */

  setProfile(nickname: string, emoji: string): void {
    write(LS_NICKNAME, nickname);
    write(LS_EMOJI, emoji);
    write(LS_NAMED, '1');
    this.patch({ nickname, emoji, hasChosenName: true });
    this.send({ t: 'set_profile', nickname, emoji });
  }

  listRooms(): void {
    this.send({ t: 'list_rooms' });
  }

  createRoom(input: CreateRoomInput): void {
    this.patch({ joinError: null });
    this.send({ t: 'create_room', input });
  }

  joinRoom(roomId: string, password?: string): void {
    this.lastJoinRoomId = roomId;
    this.patch({ joinError: null, passwordPromptFor: null });
    this.send({ t: 'join_room', roomId, ...(password ? { password } : {}) });
  }

  clearPasswordPrompt(): void {
    this.patch({ passwordPromptFor: null, joinError: null });
  }

  leaveRoom(): void {
    this.send({ t: 'leave_room' });
    this.patch({ room: null, authoritative: null, feed: [] });
  }

  setReady(ready: boolean): void {
    this.send({ t: 'set_ready', ready });
  }

  updateRules(rules: Partial<RoomRules>): void {
    this.send({ t: 'update_rules', rules });
  }

  setBots(count: number): void {
    this.send({ t: 'set_bots', count });
  }

  sendChat(text: string): void {
    this.send({ t: 'chat', text });
  }

  kick(playerId: string): void {
    this.send({ t: 'kick', playerId });
  }

  /**
   * Sends a game action against the seq we currently believe in.
   * If someone else's action lands first the server bumps seq and rejects
   * this one, and `action_rejected` restores the true state.
   */
  act(action: GameAction): void {
    const room = this.state.room;
    if (!room || this.state.pending) return;

    this.patch({ room: applyOptimistic(room, action), pending: true });
    this.send({ t: 'action', seq: room.seq, action });

    this.clearActionTimer();
    this.actionTimer = window.setTimeout(() => {
      this.actionTimer = null;
      this.patch({ room: this.state.authoritative ?? this.state.room, pending: false });
    }, ACTION_TIMEOUT_MS);
  }

  private clearActionTimer(): void {
    if (this.actionTimer !== null) {
      window.clearTimeout(this.actionTimer);
      this.actionTimer = null;
    }
  }
}

export const net = new Net();
